import React, { useState, useEffect } from 'react';
import { Plus, Users, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const GroupsManagement = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [bets, setBets] = useState([]);
  const [groupStats, setGroupStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month'); // Padrão: Mês Atual
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (user) {
      loadGroups();
      loadBets();
    }
  }, [user]);

  useEffect(() => {
    if (groups.length > 0 && bets.length >= 0) {
      calculateGroupStats();
    }
  }, [groups, bets, selectedPeriod, customStartDate, customEndDate]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBets = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBets(data);
      }
    } catch (error) {
      console.error('Error loading bets:', error);
    }
  };

  const getPeriodDates = () => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedPeriod) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'all_time':
        return null; // No filter - show all data
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const calculateGroupStats = () => {
    const periodDates = getPeriodDates();
    
    // Filtrar apostas pelo período
    let filteredBets = bets;
    if (periodDates) {
      const start = new Date(periodDates.startDate);
      const end = new Date(periodDates.endDate);
      filteredBets = bets.filter(bet => {
        const betDate = new Date(bet.date);
        return betDate >= start && betDate <= end;
      });
    }

    const stats = {};
    
    groups.forEach(group => {
      const groupBets = filteredBets.filter(bet => bet.group_id === group.id);
      
      const totalBets = groupBets.length;
      const greenBets = groupBets.filter(bet => bet.result === 'green').length;
      const totalVolume = groupBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
      const totalProfit = groupBets.reduce((sum, bet) => sum + (bet.profit || 0), 0);
      const winRate = totalBets > 0 ? (greenBets / totalBets) * 100 : 0;
      
      stats[group.id] = {
        totalBets,
        greenBets,
        totalVolume,
        totalProfit,
        winRate
      };
    });
    
    setGroupStats(stats);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name,
        description: group.description || ''
      });
    } else {
      setEditingGroup(null);
      setFormData({ name: '', description: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        description: formData.description || null
      };

      let response;
      if (editingGroup) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${editingGroup.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingGroup ? 'Grupo atualizado com sucesso!' : 'Grupo criado com sucesso!');
        closeModal();
        loadGroups();
        loadBets(); // Reload bets to recalculate stats
      } else {
        throw new Error('Falha ao salvar');
      }
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!confirm('Tem certeza que deseja excluir este grupo?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${groupId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Grupo excluído com sucesso!');
        loadGroups();
        loadBets(); // Reload bets to recalculate stats
      } else {
        throw new Error('Falha ao excluir');
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Erro ao excluir. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Grupos
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Gerencie os grupos de apostas que você segue
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span className="text-sm sm:text-base">Novo Grupo</span>
        </button>
      </div>

      {/* Period Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📅 Período dos Dados
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="current_month">📅 Mês Atual</option>
              <option value="last_month">📆 Mês Anterior</option>
              <option value="last_3_months">📊 Últimos 3 Meses</option>
              <option value="all_time">🌐 Todo o Período</option>
              <option value="custom">🎯 Período Personalizado</option>
            </select>
          </div>
          
          {selectedPeriod === 'custom' && (
            <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data inicial"
              />
              <span className="hidden sm:inline text-gray-500 dark:text-gray-400">até</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Data final"
              />
            </div>
          )}
          
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 sm:mt-6">
            {selectedPeriod === 'current_month' && '📊 Mostrando resultado do mês atual'}
            {selectedPeriod === 'last_month' && '📊 Mostrando resultado do mês passado'}
            {selectedPeriod === 'last_3_months' && '📊 Mostrando resultado dos últimos 3 meses'}
            {selectedPeriod === 'all_time' && '📊 Mostrando resultado de todo o período'}
            {selectedPeriod === 'custom' && customStartDate && customEndDate && 
              `📊 Período: ${new Date(customStartDate).toLocaleDateString('pt-BR')} - ${new Date(customEndDate).toLocaleDateString('pt-BR')}`
            }
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Grupos Cadastrados
          </h3>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Carregando...</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Nenhum grupo cadastrado
              </h3>
              <p className="text-gray-500 mb-6">
                Comece adicionando os grupos de apostas que você segue.
              </p>
              <button
                onClick={() => openModal()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Adicionar Primeiro Grupo
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-6">
              {groups.map(group => {
                const stats = groupStats[group.id] || { totalBets: 0, greenBets: 0, totalVolume: 0, totalProfit: 0, winRate: 0 };
                return (
                  <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">{group.name}</h4>
                            {group.description && (
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{group.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(group.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>

                        {/* Statistics Grid - Mobile Optimized */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mt-4">
                          <div className="text-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-600/30 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Lucro</p>
                            <p className={`text-sm sm:text-lg font-bold ${stats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {formatCurrency(stats.totalProfit)}
                            </p>
                          </div>
                          <div className="text-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-600/30 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Acerto</p>
                            <p className="text-sm sm:text-lg font-bold text-blue-600 dark:text-blue-400">
                              {stats.winRate.toFixed(2)}%
                            </p>
                          </div>
                          <div className="text-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-600/30 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Volume</p>
                            <p className="text-sm sm:text-lg font-bold text-purple-600 dark:text-purple-400 truncate">
                              {formatCurrency(stats.totalVolume)}
                            </p>
                          </div>
                          <div className="text-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-600/30 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Apostas</p>
                            <p className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white">
                              {stats.totalBets}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Actions - Mobile friendly */}
                      <div className="flex sm:flex-col items-center justify-end gap-2 pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => openModal(group)}
                          className="flex-1 sm:flex-none p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => handleDelete(group.id)}
                          className="flex-1 sm:flex-none p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal - Mobile Optimized */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Grupo *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Grupo VIP, Free Tips..."
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição (Opcional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Descrição do grupo..."
                  className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end sm:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : editingGroup ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsManagement;